import { useState, useEffect } from 'react'
import {
  BoardState,
  PlayerId,
  checkWin,
  getLegalMoves,
} from '../lib/game-logic'

export type GameStatus = 'playing' | 'won' | 'draw'

export function useGameState() {
  const [board, setBoard] = useState<BoardState>({})
  const [currentPlayer, setCurrentPlayer] = useState<PlayerId>(1)
  const [dials, setDials] = useState<{ a: number; b: number }>({ a: 1, b: 1 })
  const [previousDials, setPreviousDials] = useState<{
    a: number
    b: number
  } | null>(null)
  const [lockedDial, setLockedDial] = useState<'a' | 'b' | null>(null)
  const [status, setStatus] = useState<GameStatus>('playing')
  const [winner, setWinner] = useState<PlayerId | null>(null)
  const [lastMoveProduct, setLastMoveProduct] = useState<number | null>(null)
  const [turnCount, setTurnCount] = useState(0)
  const [hintsRemaining, setHintsRemaining] = useState(3)

  const product = dials.a * dials.b
  const isProductOccupied = !!board[product]

  const canPlace = !isProductOccupied && status === 'playing'

  const handleDialChange = (dial: 'a' | 'b', value: number) => {
    if (status !== 'playing') return

    setDials((prev) => ({ ...prev, [dial]: value }))
  }

  const resetDials = () => {
    if (previousDials) {
      setDials(previousDials)
      setLockedDial(null)
    }
  }

  const getHint = (): { a: number; b: number; product: number } | null => {
    if (hintsRemaining <= 0) return null

    const legalMoves = getLegalMoves(board, dials)
    if (legalMoves.length === 0) return null

    // Pick a random legal move
    const randomProduct =
      legalMoves[Math.floor(Math.random() * legalMoves.length)]

    // Find a valid dial combination for this product
    for (let a = 1; a <= 9; a++) {
      for (let b = 1; b <= 9; b++) {
        if (a * b === randomProduct) {
          // Check if this move respects the "keep one dial" rule
          if (turnCount === 0 || a === dials.a || b === dials.b) {
            setHintsRemaining((prev) => prev - 1)
            return { a, b, product: randomProduct }
          }
        }
      }
    }

    return null
  }

  const placeChip = () => {
    if (!canPlace) return

    const newBoard = { ...board, [product]: currentPlayer }
    setBoard(newBoard)
    setLastMoveProduct(product)
    setPreviousDials({ ...dials })

    if (checkWin(newBoard, product, currentPlayer)) {
      setStatus('won')
      setWinner(currentPlayer)
      return
    }

    const nextPlayer = currentPlayer === 1 ? 2 : 1
    setCurrentPlayer(nextPlayer)
    setTurnCount((prev) => prev + 1)
  }

  return {
    board,
    currentPlayer,
    dials,
    product,
    status,
    winner,
    lastMoveProduct,
    turnCount,
    hintsRemaining,
    handleDialChange,
    placeChip,
    canPlace,
    resetDials,
    getHint,
    resetGame: () => {
      setBoard({})
      setCurrentPlayer(1)
      setDials({ a: 1, b: 1 })
      setPreviousDials(null)
      setStatus('playing')
      setWinner(null)
      setTurnCount(0)
      setLastMoveProduct(null)
      setHintsRemaining(3)
    },
  }
}
